import { ViewBooking } from "collins-js-sdk";
import { Hexagon } from "lucide-react";
import React from "react";
import { bookingsPreorder } from "../../../../../../../types/bookings";
import { VenueConfig } from "../../../../../../../types/venues";
import { getCurrencySymbol } from "../../../../../../../utils/number";
import { viewPreorderItemToV4 } from "../../../../../../../utils/preorderMapper";
import { Skeleton } from "../../../../../../ui/skeleton";
import { WebViewTooltip } from "../../../../SubHeader/WebViewTooltip";
import { getPreorderTotalValue } from "../utils/bookingCardUtils";

interface DiaryBookingCardPreorderBannerProps {
  booking: ViewBooking;
  showDetailedLabels?: boolean;
  venueConfig?: VenueConfig;
  isInitialising?: boolean;
}

export const DiaryBookingCardPreorderBanner =
  React.memo<DiaryBookingCardPreorderBannerProps>(
    ({ booking, showDetailedLabels, venueConfig, isInitialising }) => {
      if (!showDetailedLabels || !booking.hasPreorders) {
        return null;
      }

      // Use the fetched preorder data if available, otherwise fall back to booking data
      const preordersData = booking.preorders ?? [];

      const activePreOrders = preordersData.filter(
        (preOrder: bookingsPreorder) =>
          !preOrder.status || preOrder.status !== "deleted"
      );

      // Count total food items across all preorders, not just number of preorder transactions
      const totalFoodItems = activePreOrders.reduce(
        (total: number, preOrder: bookingsPreorder) => {
          return total + (preOrder.items?.length ?? 0);
        },
        0
      );

      const itemCount = totalFoodItems;
      const totalValue = getPreorderTotalValue({
        ...booking,
        preorders: preordersData
      });

      // Show loading state while fetching
      if (isInitialising) {
        return (
          <div className="DiarySidebarListItem__preorderBanner">
            <div className="DiarySidebarListItem__preorderBanner__content">
              <Hexagon
                size={16}
                className="DiarySidebarListItem__preorderBanner__icon"
              />
              <Skeleton className="h-4 w-32 rounded" />
            </div>
          </div>
        );
      }

      // Don't show banner if no active preorders
      if (itemCount === 0) {
        return null;
      }

      // Helper function to collect preorder items
      const collectPreorderItems = (preOrders: bookingsPreorder[]) => {
        return (
          preOrders.flatMap((preOrder) =>
            preOrder.items?.map((item) => {
              const converted = viewPreorderItemToV4(item);
              return {
                ...converted,
                name: converted.name ?? "Unknown item"
              };
            })
          ) ?? []
        );
      };

      // Build tooltip content showing preorder items
      const buildTooltipContent = () => {
        const allItems = collectPreorderItems(activePreOrders);

        const maxItems = 4;
        const displayItems = allItems.slice(0, maxItems);
        const remainingCount = allItems.length - maxItems;

        const currencySymbol = getCurrencySymbol(
          venueConfig?.currency ?? "GBP"
        );

        return (
          <div style={{ minWidth: "280px", maxWidth: "350px" }}>
            {displayItems.map((item, idx) => (
              <div
                key={`${item.name}-${item.quantity}-${idx}`}
                style={{
                  marginBottom: "8px",
                  paddingBottom: "8px",
                  borderBottom:
                    idx < displayItems.length - 1
                      ? "1px solid rgba(128, 128, 128, 0.2)"
                      : "none"
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "2px"
                  }}
                >
                  <span style={{ fontWeight: 600 }}>
                    {item.quantity}x {item.name}
                  </span>
                  <span style={{ fontWeight: 600, marginLeft: "12px" }}>
                    {currencySymbol}
                    {item?.total?.toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
            {remainingCount > 0 && (
              <div
                style={{
                  marginTop: "12px",
                  paddingTop: "8px",
                  borderTop: "1px solid rgba(128, 128, 128, 0.2)",
                  fontStyle: "italic",
                  opacity: 0.8
                }}
              >
                +{remainingCount} more item{remainingCount !== 1 ? "s" : ""}
              </div>
            )}
          </div>
        );
      };

      return (
        <div className="DiarySidebarListItem__preorderBanner">
          <div className="DiarySidebarListItem__preorderBanner__content">
            <Hexagon
              size={16}
              className="DiarySidebarListItem__preorderBanner__icon"
            />
            <WebViewTooltip content={buildTooltipContent()} side="bottom">
              <span className="DiarySidebarListItem__preorderBanner__text">
                Pre-order: {itemCount} items •{" "}
                {getCurrencySymbol(venueConfig?.currency ?? "GBP")}
                {totalValue}
              </span>
            </WebViewTooltip>
          </div>
        </div>
      );
    }
  );

DiaryBookingCardPreorderBanner.displayName = "DiaryBookingCardPreorderBanner";
